import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Loader, Svg, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Routes } from '@/_shared/constants/routes';
import { getDepartmentTeamsCall } from '@/departments/state';
import { getEmployeesMeCall } from '@/profile/state';

export const TeamsDepartment = () => {
  const t = useTranslations('dashboard.learning.teamDepartment');
  const tShared = useTranslations();
  const navigate = useNavigate();
  const { leadership, fullName, shortName, role } = getEmployeesMeCall.hook();

  const {
    execute: executeGetDepartmentTeams,
    loading: loadingGetDepartmentTeams,
    error: errorGetDepartmentTeams,
    response: responseGetDepartmentTeams,
  } = getDepartmentTeamsCall.hook();

  const goToDepartment = () => {
    navigate(Routes.Department);
  };

  const goToTeam = (teamId: string) => {
    navigate(Routes.DepartmentTeamDetails.replace(':teamId', teamId));
  };

  useEffect(() => {
    executeGetDepartmentTeams({
      departmentId: leadership?.department?.id || '',
    });
  }, [executeGetDepartmentTeams, leadership]);

  if (loadingGetDepartmentTeams) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetDepartmentTeams) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  return (
    <Card p='s'>
      <Box
        alignItems='center'
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        mt='xxxs'
        pb='s'
      >
        <Typography variant='h3'>{leadership?.department?.name}</Typography>
        <Box alignItems='center' flexDirection='row' display='flex'>
          <Svg name='users' size='xs' />
          <Typography ml='xxs' variant='h3'>
            {responseGetDepartmentTeams?.department.stats?.totalEmployees || 0}
          </Typography>
        </Box>
      </Box>
      <Box alignItems='center' display='flex' flexDirection='row' mb='s'>
        <Box
          backgroundColor='primary'
          borderRadius='xl'
          alignItems='center'
          justifyContent='center'
          width='xxl'
          height='xxl'
          display='flex'
          flexDirection='row'
        >
          <Typography
            display='inline-flex'
            justifyContent='center'
            alignItems='center'
            variant='h1'
          >
            {shortName || ''}
          </Typography>
        </Box>
        <Box display='flex' flexDirection='column'>
          <Typography ml='xs' variant='h3'>
            {fullName || ''}
          </Typography>
          <Typography ml='xs' fontSize='xs' variant='p3/light'>
            {role || ''}
          </Typography>
        </Box>
      </Box>
      {responseGetDepartmentTeams && responseGetDepartmentTeams?.data?.length > 0 && (
        <Box mb='m'>
          <Typography variant='p3/label'>{t('title')}</Typography>
          <Box>
            {responseGetDepartmentTeams?.data.map(team => (
              <Card p='s' key={team.name} mt='xs' backgroundColor='grey/dark/background'>
                <Box
                  alignItems='center'
                  display='flex'
                  flexDirection='row'
                  justifyContent='space-between'
                  pb={team.id !== 'unknown' ? 'xs' : 'none'}
                >
                  <Typography variant='h4'>
                    {team.id !== 'unknown' ? team.name : t('noDepartmentName')}
                  </Typography>
                  <Box alignItems='center' flexDirection='row' display='flex'>
                    <Svg name='users' size='xs' />
                    <Typography ml='xxs' variant='h3'>
                      {team.totalEmployees}
                    </Typography>
                  </Box>
                </Box>
                {team.id !== 'unknown' && (
                  <Button onClick={() => goToTeam(team.id)} variant='positive' fontSize='s'>
                    {t('ctaViewTeam')}
                  </Button>
                )}
              </Card>
            ))}
          </Box>
        </Box>
      )}
      <Button
        onClick={goToDepartment}
        variant='primary'
        display='block'
        style={{ margin: '0 auto' }}
      >
        {t('cta')}
      </Button>
    </Card>
  );
};
